import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import classNames from 'classnames';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import BroadcastMessageModal from '../BroadcastMessageModal';
import BroadcastPulseCheckModal from '../BroadcastPulseCheckModal';
import { Lead } from 'models/Lead';
import ActionListModal from 'components/ActionListModal';
import MessageIcon from 'icons/Message.icon';
import BellIcon from 'icons/Bell.icon';
import { COLOR_VARIANT } from 'models/Colors';

import './BroadcastModal.styles.scss';

type BroadcastModalProps = {
  className?: string;
  dealRoomId: string;
  leads: Partial<Lead>[];
} & ModalProps;

const BroadcastModal: React.ForwardRefRenderFunction<
  ModalRef,
  BroadcastModalProps
> = (props, ref) => {
  const { className, dealRoomId, leads, ...rest } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const [messageModal, messageModalRef] = useCallbackRef<ModalRef>();
  const [pulseCheckModal, pulseCheckModalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('broadcast-modal', className);

  const { t } = useTranslation();

  const items = useMemo(
    () => [
      {
        title: t('broadcastMessage'),
        description: t('sendAMessageToAllActiveLeads'),
        Icon: MessageIcon,
        onClick: () => messageModal?.open(),
      },
      {
        title: t('broadcastPulseCheck'),
        description: t('checkInterestOfActiveLeads'),
        color: COLOR_VARIANT.ORANGE,
        Icon: BellIcon,
        onClick: () => pulseCheckModal?.open(),
      },
    ],
    [messageModal, pulseCheckModal, t],
  );

  return (
    <>
      <ActionListModal
        ref={modalRef}
        className={classes}
        items={items}
        title={t('broadcastInformation')}
        description={t('whatWouldYouLikeToBroadcast')}
        {...rest}
      />
      <BroadcastMessageModal
        ref={messageModalRef}
        dealRoomId={dealRoomId}
        onOpenPulseCheck={pulseCheckModal?.open}
      />
      <BroadcastPulseCheckModal
        ref={pulseCheckModalRef}
        dealRoomId={dealRoomId}
        leads={leads}
        openBroadcastMessage={messageModal?.open}
      />
    </>
  );
};

export default forwardRef(BroadcastModal);
